import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

/**
 * Window-based row virtualization for the PWA library grid.
 *
 * Mirrors the desktop `useVirtualizedMovieGrid` row-based windowing but
 * uses document (window) scroll instead of a fixed-height inner scroll
 * container, because `LibraryView` flows naturally under a sticky header
 * and the whole page scrolls as one.
 *
 * How it works:
 *
 *   1. A `ResizeObserver` on the outer grid element measures
 *      `containerWidth` whenever the viewport or layout changes. The
 *      column count is derived from `containerWidth` / the responsive
 *      breakpoint table below.
 *   2. A window `scroll` listener computes the grid's offset in the
 *      document (`gridTop = rect.top + window.scrollY`) and uses
 *      `scrollTop = window.scrollY` / `viewportHeight = window.innerHeight`
 *      to figure out which rows fall inside the viewport. `overscan` adds
 *      a few rows of padding so rows that are about to scroll in are
 *      already mounted.
 *   3. The hook returns a `gridLayout` (columns / row count / total
 *      height), a `visibleRows` array (the row indices that should
 *      render), and the `containerRef` to attach to the outer grid div.
 *
 * The hook is pure apart from the ResizeObserver and window scroll
 * listener — both of which clean up on unmount. No refs, stores, or side
 * effects beyond that.
 *
 * Consumers render an outer `<div ref={containerRef} style={{ height:
 * totalHeight, position: 'relative' }}>` and then, for each row index in
 * `visibleRows`, an absolutely-positioned row at `top: row * rowHeight`
 * holding `columns` cards of width `cardWidth`.
 */

export interface VirtualizedGridBreakpoint {
  /** Minimum container width (inclusive) this breakpoint applies to. */
  minWidth: number
  /** Number of columns when the container is at least `minWidth` wide. */
  columns: number
}

/**
 * Default column breakpoints — mirrors the old static Tailwind
 * `grid-cols-2 sm:3 md:4 lg:5 xl:6 2xl:8` the library grid was using,
 * so the visual rhythm is unchanged.
 */
export const DEFAULT_BREAKPOINTS: VirtualizedGridBreakpoint[] = [
  { minWidth: 0, columns: 2 },
  { minWidth: 640, columns: 3 },
  { minWidth: 768, columns: 4 },
  { minWidth: 1024, columns: 5 },
  { minWidth: 1280, columns: 6 },
  { minWidth: 1536, columns: 8 },
]

export interface UseVirtualizedGridOptions {
  /** Total number of items to virtualize. */
  itemCount: number
  /**
   * Gap (in px) between cards in both directions. Must match the gap
   * used inside the row renderer — defaults to 16 (Tailwind `gap-4`).
   */
  gap?: number
  /**
   * Extra rows to render above and below the visible window so the
   * user never sees a blank row when scrolling quickly. Default 2.
   */
  overscan?: number
  /**
   * Aspect ratio of each card (width / height). Poster cards use 2/3.
   * Note: because we also add some chrome height for the title/year
   * strip, set `extraRowHeight` to any pixel height on top of the
   * aspect ratio.
   */
  aspectRatio?: number
  /** Pixel height added to each row on top of the aspect ratio. */
  extraRowHeight?: number
  /** Optional responsive breakpoints; defaults to `DEFAULT_BREAKPOINTS`. */
  breakpoints?: VirtualizedGridBreakpoint[]
  /**
   * Enables virtualization. When false the hook returns every row as
   * visible, which is handy for tests or feature flags.
   */
  enabled?: boolean
}

export interface VirtualizedGridLayout {
  columns: number
  rowCount: number
  rowHeight: number
  cardWidth: number
  cardHeight: number
  gap: number
  totalHeight: number
  containerWidth: number
}

export interface VirtualizedGridResult {
  containerRef: (el: HTMLDivElement | null) => void
  layout: VirtualizedGridLayout
  /** Row indices that should be rendered (includes overscan). */
  visibleRows: number[]
  /**
   * Convert a flat item index to its grid coordinates. Returns `null`
   * if the index is out of bounds.
   */
  indexToCoords: (index: number) => { row: number; col: number } | null
  /** Convert `(row, col)` back to a flat item index, clamped to bounds. */
  coordsToIndex: (row: number, col: number) => number
}

function columnsForWidth(
  width: number,
  breakpoints: VirtualizedGridBreakpoint[]
): number {
  let cols = 1
  for (const bp of breakpoints) {
    if (width >= bp.minWidth) cols = bp.columns
  }
  return Math.max(1, cols)
}

export function useVirtualizedGrid({
  itemCount,
  gap = 16,
  overscan = 2,
  aspectRatio = 2 / 3,
  extraRowHeight = 0,
  breakpoints = DEFAULT_BREAKPOINTS,
  enabled = true,
}: UseVirtualizedGridOptions): VirtualizedGridResult {
  const [containerWidth, setContainerWidth] = useState(0)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 0
  )
  const [gridOffsetTop, setGridOffsetTop] = useState(0)

  const elementRef = useRef<HTMLDivElement | null>(null)
  const rafRef = useRef<number | null>(null)

  // Recompute the grid's offset inside the document. Called on scroll,
  // resize, and after every layout pass so sticky headers / content
  // above the grid don't desynchronise the virtualization window.
  const recomputeOffset = useCallback(() => {
    const node = elementRef.current
    if (!node) return
    const rect = node.getBoundingClientRect()
    setGridOffsetTop(rect.top + window.scrollY)
  }, [])

  const containerRef = useCallback(
    (el: HTMLDivElement | null) => {
      elementRef.current = el
      if (el) {
        setContainerWidth(el.getBoundingClientRect().width)
        // Defer so layout is stable on first paint.
        requestAnimationFrame(recomputeOffset)
      }
    },
    [recomputeOffset]
  )

  // ResizeObserver on the outer grid element tracks column count
  // changes. Also listen to window resize for viewport height.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const el = elementRef.current
    if (!el) return

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.contentRect.width
          setContainerWidth((prev) => (prev === width ? prev : width))
        }
        recomputeOffset()
      })
      resizeObserver.observe(el)
    }

    const onResize = () => {
      setViewportHeight(window.innerHeight)
      setContainerWidth(el.getBoundingClientRect().width)
      recomputeOffset()
    }
    window.addEventListener('resize', onResize, { passive: true })

    return () => {
      if (resizeObserver) resizeObserver.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [recomputeOffset])

  // Window scroll → scrollTop. Throttled with rAF so very fast scrolls
  // don't re-render on every pixel.
  useEffect(() => {
    if (typeof window === 'undefined') return

    const onScroll = () => {
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        setScrollTop(window.scrollY)
      })
    }

    // Seed with the current scroll position so the first render is
    // already aligned with where the page sits.
    setScrollTop(window.scrollY)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  // Recompute the grid offset whenever the item count changes — new
  // filter results may shift the grid's height above it.
  useLayoutEffect(() => {
    recomputeOffset()
  }, [itemCount, recomputeOffset])

  const layout = useMemo<VirtualizedGridLayout>(() => {
    const columns = columnsForWidth(containerWidth, breakpoints)
    const totalGapX = gap * (columns - 1)
    const cardWidth =
      containerWidth > 0 ? Math.max(0, (containerWidth - totalGapX) / columns) : 0
    const cardHeight =
      cardWidth > 0 ? cardWidth / Math.max(0.01, aspectRatio) + extraRowHeight : 0
    const rowHeight = cardHeight + gap
    const rowCount = Math.ceil(itemCount / columns)
    const totalHeight = rowCount > 0 ? rowCount * rowHeight - gap : 0
    return {
      columns,
      rowCount,
      rowHeight,
      cardWidth,
      cardHeight,
      gap,
      totalHeight: Math.max(0, totalHeight),
      containerWidth,
    }
  }, [containerWidth, itemCount, gap, aspectRatio, extraRowHeight, breakpoints])

  const visibleRows = useMemo<number[]>(() => {
    if (!enabled || layout.rowCount === 0 || layout.rowHeight === 0) {
      // Render every row when disabled or before layout measurements
      // have settled. With the row count at zero this is a no-op.
      return Array.from({ length: layout.rowCount }, (_, i) => i)
    }

    // Offset of the scroll position relative to the grid's top edge.
    const relativeScroll = Math.max(0, scrollTop - gridOffsetTop)
    const startRow = Math.floor(relativeScroll / layout.rowHeight)
    const endRow = Math.ceil(
      (relativeScroll + viewportHeight) / layout.rowHeight
    )

    const first = Math.max(0, startRow - overscan)
    const last = Math.min(layout.rowCount - 1, endRow + overscan)

    if (last < first) return []
    const rows: number[] = []
    for (let r = first; r <= last; r++) rows.push(r)
    return rows
  }, [
    enabled,
    layout.rowCount,
    layout.rowHeight,
    scrollTop,
    gridOffsetTop,
    viewportHeight,
    overscan,
  ])

  const indexToCoords = useCallback(
    (index: number) => {
      if (index < 0 || index >= itemCount || layout.columns === 0) return null
      return {
        row: Math.floor(index / layout.columns),
        col: index % layout.columns,
      }
    },
    [itemCount, layout.columns]
  )

  const coordsToIndex = useCallback(
    (row: number, col: number) => {
      const clampedRow = Math.max(0, Math.min(layout.rowCount - 1, row))
      const clampedCol = Math.max(0, Math.min(layout.columns - 1, col))
      const idx = clampedRow * layout.columns + clampedCol
      return Math.max(0, Math.min(itemCount - 1, idx))
    },
    [itemCount, layout.columns, layout.rowCount]
  )

  return {
    containerRef,
    layout,
    visibleRows,
    indexToCoords,
    coordsToIndex,
  }
}
